import { StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

let pending = 0;
let requested = false;
let recovery = null;
let uncertain = false;
let revision = 0;
const tracked = new WeakSet();
const locked = new Map();

function lock() {
  uncertain = true;
  document.documentElement.setAttribute('data-ct-cart-uncertain', '');
  for (const element of document.querySelectorAll('cart-items-component > :not([data-ct-recovery-notice]), product-form-component')) {
    if (!locked.has(element)) locked.set(element, element.inert);
    element.inert = true;
  }
}

function unlock() {
  for (const [element, original] of locked) element.inert = original;
  locked.clear();
  uncertain = false;
  document.documentElement.removeAttribute('data-ct-cart-uncertain');
}

function notice(state) {
  for (const component of document.querySelectorAll('cart-items-component')) {
    component.querySelector(':scope > [data-ct-recovery-notice]')?.remove();
    const panel = document.createElement('div');
    panel.setAttribute('data-ct-recovery-notice', state);
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    const message = document.createElement('p');
    message.textContent = component.dataset[`ctRecovery${state[0].toUpperCase()}${state.slice(1)}`];
    panel.append(message);
    if (state !== 'loading') {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = component.dataset[state === 'failed' ? 'ctRecoveryRetry' : 'ctRecoveryDismiss'];
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        if (state === 'failed') requestRecovery();
        else panel.remove();
      });
      panel.append(button);
    }
    component.prepend(panel);
  }
}

async function getJSON(url) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    cache: 'no-store', signal: AbortSignal.timeout(12000),
  });
  if (!response.ok) throw new Error(`Cart recovery HTTP ${response.status}`);
  return response.json();
}

async function reconcile() {
  const startedAt = revision;
  lock();
  notice('loading');
  const sections = new Map([...document.querySelectorAll('cart-items-component')]
    .map(component => [component.dataset.sectionId, component.hasAttribute('data-drawer')]));
  if (!sections.size || [...sections.keys()].some(id => !id) || sections.size > 5) throw new Error('Cart recovery sections unavailable');
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('sections', [...sections.keys()].join(','));
  const [cart, html] = await Promise.all([getJSON(`${Theme.routes.cart_url}.js`), getJSON(url.toString())]);
  if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error('Invalid recovered cart');
  for (const id of sections.keys()) {
    if (typeof html[id] !== 'string' || !new DOMParser().parseFromString(html[id], 'text/html').getElementById(`shopify-section-${id}`)) {
      throw new Error(`Cart recovery section missing: ${id}`);
    }
  }
  if (pending || revision !== startedAt) { requested = true; return; }
  for (const [id, drawer] of sections) {
    await morphSection(id, html[id], { mode: drawer ? 'hydration' : 'full', injectStylesheet: true });
  }
  for (const icon of document.querySelectorAll('cart-icon')) icon.renderCartBubble?.(cart.item_count, false);
  unlock();
  notice('restored');
}

function requestRecovery() {
  requested = true;
  lock();
  const drawer = document.getElementById('cart-drawer');
  if (typeof drawer?.open === 'function' && !drawer.hasAttribute('open')) drawer.open();
  if (pending || recovery) return;
  requested = false;
  recovery = reconcile().catch((error) => {
    console.warn('[ct-cart-recovery] Unable to confirm cart:', error);
    notice('failed');
    lock();
  }).finally(() => {
    recovery = null;
    if (requested && !pending) requestRecovery();
  });
}

for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate]) {
  document.addEventListener(name, (event) => {
    if (!event.promise || tracked.has(event.promise)) return;
    tracked.add(event.promise);
    revision++;
    if (recovery) requested = true;
    pending++;
    event.promise.then((result) => {
      if (result?.detail?.sections && Object.values(result.detail.sections).some(html => typeof html !== 'string')) {
        requested = true;
        lock();
      }
    }, () => { requested = true; lock(); }).finally(() => {
      pending--;
      if (requested && !pending) requestRecovery();
    });
  });
}

document.addEventListener(StandardEvents.cartError, (event) => {
  if (event.code === 'SERVICE_UNAVAILABLE') requestRecovery();
});

window.addEventListener('online', () => { if (uncertain) requestRecovery(); });
window.addEventListener('pageshow', (event) => { if (event.persisted && uncertain) requestRecovery(); });
